import { useState } from "react";
import { Link } from "react-router-dom";
import { track } from "@/lib/analytics";
import {
  Footprints,
  Camera,
  Video,
  KeyRound,
  FileSignature,
  Wrench,
  Search,
  Signpost,
  Sparkles,
  ArrowRight,
  ExternalLink,
  ShieldCheck,
  Clock3,
  MapPin,
  Zap,
  ChevronRight,
} from "lucide-react";

/**
 * BOG (Boots on the Ground) Field Services — partner storefront.
 *
 * Every CTA on this page routes to Tracy's InvestorBootz affiliate link
 * (`join.investorbootz.com/?ref=tracy`). InvestorBootz pays Tracy per
 * signup — so the goal of this page is to convince the user to click
 * through and create an account, THEN order.
 *
 * Long-term: if we sign a deeper integration, the "Order" buttons will
 * open an embedded intake modal that POSTs directly to their API without
 * leaving iSpeedToClose.
 */

// Tracy's live InvestorBootz affiliate link — pays her per signup.
// Analytics `bog_partner_click` fires on every CTA so we can measure
// volume and quote real numbers back to InvestorBootz when negotiating
// a deeper integration.
const PARTNER_JOIN_URL = "https://join.investorbootz.com/?ref=tracy";

const SERVICES = [
  {
    id: "photos",
    icon: Camera,
    name: "Property Photos + Walk-Through",
    typicalPrice: "$75-$125",
    turnaround: "24-48 hrs",
    desc: "Full exterior + interior photo set from a vetted local rep. Photos flow straight into your property gallery.",
    fitsWhen: "You just contracted a property and need fresh photos for the Deal Room before you blast to buyers.",
    testid: "bog-service-photos",
  },
  {
    id: "walkthrough_video",
    icon: Video,
    name: "Walkthrough Video + Floor Plan",
    typicalPrice: "$150-$225",
    turnaround: "2-3 days",
    desc: "Room-by-room video with narration plus a 2D floor plan sketch. Buyers close ~30% faster with a video available.",
    fitsWhen: "You're marketing a higher-ticket deal (>$150k) and want to command a higher assignment fee.",
    testid: "bog-service-walkthrough_video",
  },
  {
    id: "lockbox",
    icon: KeyRound,
    name: "Lockbox Install + Removal",
    typicalPrice: "$45-$75",
    turnaround: "Same day",
    desc: "BootzForce Rep installs your lockbox (or a rental one) so buyers can self-tour. Removal after closing included.",
    fitsWhen: "You have multiple buyers wanting to walk the property before making an offer.",
    testid: "bog-service-lockbox",
  },
  {
    id: "notary",
    icon: FileSignature,
    name: "Mobile Notary + Document Runner",
    typicalPrice: "$75-$150",
    turnaround: "24 hrs",
    desc: "Seller doesn't want to leave the house? Rep drives out with the PA, gets it notarized, and delivers to title.",
    fitsWhen: "A seller is stalling on signing — bring the closing table to their kitchen.",
    testid: "bog-service-notary",
  },
  {
    id: "inspection",
    icon: Search,
    name: "Inspection + Occupancy Check",
    typicalPrice: "$95-$175",
    turnaround: "48 hrs",
    desc: "Verify occupancy, condition, and access. Photo evidence delivered same day. Perfect for tax-delinquent and pre-foreclosure lists.",
    fitsWhen: "You bought a driving-for-dollars list and need to prove condition before making offers.",
    testid: "bog-service-inspection",
  },
  {
    id: "signage",
    icon: Signpost,
    name: "Sign Placement + Marketing",
    typicalPrice: "$45-$95",
    turnaround: "Same day",
    desc: `"WE BUY HOUSES" or "COMING SOON" sign install with photo confirmation. Sign removal included.`,
    fitsWhen: "You want passive lead capture from drive-by traffic in your target neighborhoods.",
    testid: "bog-service-signage",
  },
  {
    id: "preservation",
    icon: Wrench,
    name: "Preservation + Cleanout",
    typicalPrice: "$150-$500",
    turnaround: "3-5 days",
    desc: "Lawn care, debris removal, board-ups, minor handyman work. Keep your under-contract properties city-code-clean.",
    fitsWhen: "You inherited a property mid-contract and code enforcement is knocking.",
    testid: "bog-service-preservation",
  },
];

const HOW_IT_WORKS = [
  { icon: MapPin, title: "Pick a property + service", body: "Choose from your pipeline or paste an address. Select what you need — photos, walk, lockbox, inspection." },
  { icon: Zap,    title: "InvestorBootz dispatches", body: "A rated BootzForce™ Rep in that market is dispatched within hours. You don't manage them — they handle the whole chain." },
  { icon: ShieldCheck, title: "Assets land in your gallery", body: "Photos, videos, floor plans, and reports appear in your property record automatically. Ready to send to buyers." },
];

export default function BogFieldServices() {
  const [addr, setAddr] = useState("");

  const fireOrder = (serviceId) => {
    track("bog_partner_click", { service: serviceId, has_address: !!addr.trim() });
    window.open(PARTNER_JOIN_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="bog-field-services-page">
      {/* ── Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              "radial-gradient(ellipse at 20% 0%, #F59E0B33 0%, transparent 55%), radial-gradient(ellipse at 90% 30%, #0B1D3B22 0%, transparent 50%)",
          }}
        />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 pt-10 pb-8">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-[0.14em] text-amber-700 bg-amber-100 border border-amber-300 rounded-full px-3 py-1">
              <Footprints size={12} /> Boots on the Ground
            </span>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 bg-white/70 border border-slate-200 rounded-full px-3 py-1">
              <Sparkles size={11} className="text-amber-600" /> Powered by InvestorBootz
            </span>
          </div>
          <h1 className="font-display font-black tracking-tighter text-4xl md:text-5xl text-slate-900 mb-3 leading-[1.02]">
            Send a rep to any US property.
            <br />
            <span className="text-amber-700">Get the assets back in 48 hours.</span>
          </h1>
          <p className="text-slate-600 text-base md:text-lg max-w-2xl leading-relaxed mb-6">
            Vetted BootzForce™ Reps in all 50 states, Guam, and Puerto Rico. Order property
            photos, walkthroughs, lockbox installs, inspections, notaries — all through
            one dashboard, no local vendor management required.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={PARTNER_JOIN_URL}
              target="_blank" rel="noopener noreferrer"
              onClick={() => track("bog_partner_click", { service: "landing_cta" })}
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-br from-amber-600 to-amber-800 hover:from-amber-700 hover:to-amber-900 text-white text-sm font-bold px-5 py-3 shadow-md transition-all"
              data-testid="bog-hero-primary-cta"
            >
              <Footprints size={16} /> Open a BootzForce account
              <ExternalLink size={12} />
            </a>
            <Link
              to="/v3/crm/pipeline"
              className="inline-flex items-center gap-2 rounded-lg bg-white hover:bg-slate-50 border border-slate-300 text-slate-800 text-sm font-bold px-5 py-3 transition-colors"
              data-testid="bog-hero-pipeline-cta"
            >
              Order from a pipeline property
              <ChevronRight size={14} />
            </Link>
          </div>
          <div className="mt-4 flex items-center gap-4 text-xs text-slate-500">
            <span className="inline-flex items-center gap-1">
              <ShieldCheck size={11} className="text-emerald-600" /> Insured
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock3 size={11} className="text-amber-600" /> Avg 24-48 hr turnaround
            </span>
            <span className="inline-flex items-center gap-1">
              <MapPin size={11} className="text-slate-600" /> All 50 states
            </span>
          </div>
        </div>
      </section>

      {/* ── Quick order — paste an address */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          <h2 className="font-display font-bold text-xl text-slate-900 mb-2">
            Fastest path: paste an address
          </h2>
          <p className="text-sm text-slate-600 mb-4">
            Drop the property, pick a service. We&rsquo;ll hand it off to InvestorBootz
            with the address pre-filled.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="text"
              value={addr}
              onChange={(e) => setAddr(e.target.value)}
              placeholder="123 Main St, Philadelphia, PA 19121"
              className="flex-1 rounded-lg border border-slate-300 px-4 py-3 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-200 outline-none"
              data-testid="bog-quick-order-address"
            />
            <button
              type="button"
              onClick={() => fireOrder("quick_order")}
              disabled={!addr.trim()}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-5 py-3 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              data-testid="bog-quick-order-submit"
            >
              Continue on InvestorBootz <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </section>

      {/* ── Services grid */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-8">
        <div className="flex items-end justify-between flex-wrap gap-3 mb-5">
          <div>
            <h2 className="font-display font-bold text-2xl text-slate-900 tracking-tight">
              What you can order
            </h2>
            <p className="text-sm text-slate-600 mt-1">
              Every service dispatches a local, insured, rated BootzForce™ Rep.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} service={s} onOrder={fireOrder} />
          ))}
        </div>
      </section>

      {/* ── How it works */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10">
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-8 text-white">
          <h2 className="font-display font-bold text-2xl mb-1 tracking-tight">
            How it works
          </h2>
          <p className="text-slate-300 text-sm mb-6">
            You never touch the field vendor. InvestorBootz coordinates, verifies, and delivers.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {HOW_IT_WORKS.map((h, i) => (
              <div key={h.title} className="relative">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-9 h-9 rounded-lg bg-amber-500/20 border border-amber-500/40 text-amber-300 flex items-center justify-center">
                    <h.icon size={17} />
                  </div>
                  <div className="text-[11px] font-black uppercase tracking-[0.14em] text-amber-400">
                    Step {i + 1}
                  </div>
                </div>
                <h3 className="font-bold text-lg mb-1">{h.title}</h3>
                <p className="text-sm text-slate-300 leading-relaxed">{h.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Partnership disclosure */}
      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 pb-16">
        <div className="border-t border-slate-200 pt-6 text-xs text-slate-500 leading-relaxed">
          Boots on the Ground is powered by our field-services partner{" "}
          <a
            href={PARTNER_JOIN_URL}
            target="_blank" rel="noopener noreferrer"
            className="underline hover:text-slate-700"
          >
            InvestorBootz
          </a>. Prices shown are typical ranges quoted by InvestorBootz — exact quotes are
          generated per job on their platform. iSpeedToClose does not employ or vet
          individual field reps; all services are dispatched by InvestorBootz under their
          own service agreement.
        </div>
      </section>
    </div>
  );
}

function ServiceCard({ service, onOrder }) {
  const Icon = service.icon;
  return (
    <div
      className="group bg-white rounded-2xl border border-slate-200 hover:border-amber-300 hover:shadow-md p-5 transition-all"
      data-testid={service.testid}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-500 to-amber-700 text-white flex items-center justify-center shrink-0 shadow-sm">
          <Icon size={20} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-base text-slate-900 leading-tight">
            {service.name}
          </div>
          <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
            <span className="font-semibold text-slate-700 tabular-nums">{service.typicalPrice}</span>
            <span className="text-slate-300">·</span>
            <span>{service.turnaround}</span>
          </div>
        </div>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-3">{service.desc}</p>
      <div className="bg-amber-50/60 border border-amber-200/60 rounded-md px-3 py-2 mb-4">
        <div className="text-[10px] font-black uppercase tracking-[0.14em] text-amber-700 mb-0.5">
          Order this when
        </div>
        <div className="text-[13px] text-slate-800 leading-snug">{service.fitsWhen}</div>
      </div>
      <button
        type="button"
        onClick={() => onOrder(service.id)}
        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-bold px-4 py-2.5 transition-colors"
        data-testid={`${service.testid}-order`}
      >
        Order via InvestorBootz <ExternalLink size={12} />
      </button>
    </div>
  );
}
